import { Button, DynamicContainer, Form, FormContext } from '@hai/ui-react';
import { ButtonStateType } from '@hai/ui-react/dist/types';
import { FormikProps } from 'formik';
import { range } from 'ramda';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormTextfield } from 'src/js/component/base/form/form-textfield';
import { FormBuilder, FormFieldType, FormLayout } from 'src/js/component/form-builder';
import { triggerEvent } from 'src/js/events';
import { useStores } from 'src/js/hook/use-stores';
import { validateFormContext } from 'src/js/validator/validator';

import { ScteViewModel, scteValidationSchema } from './scte-models';
import scteModels, { updateScte } from './scte-store';

interface Props {
  model: ScteViewModel;
  onCancel: VoidFunction;
}

export const ScteSettings: React.FunctionComponent<Props> = ({ model, onCancel }) => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const formRef = useRef<FormikProps<ScteViewModel>>();
  const [settings, setSettings] = useState<ScteViewModel>(model);

  const [buttonState, setButtonState] = useState<ButtonStateType>(undefined);

  const preEventReminderOptions = range(1, 6).map((v) => ({
    value: v,
    label: v.toString(),
  }));

  const syncTypeOptions = [
    { value: 'auto', label: t('scte.syncTypes.auto') },
    { value: 'alive', label: t('scte.syncTypes.alive') },
    { value: 'timeDescriptor', label: t('scte.syncTypes.timeDescriptor') },
    { value: 'system', label: t('scte.syncTypes.system') },
  ];

  const timeDescriptorPeriodOptions = range(0, 61).map((v) => ({
    value: v,
    label: v === 0 ? `0 (${t('general.disabled')})` : v.toString(),
  }));

  const handleSubmit = (values: any) => {
    setButtonState('pending');
    return updateScte(values, t).then((res) => {
      if (res.ok) {
        const scte = scteModels.find((e) => e.id === model.id);
        setSettings(scte); // update edit view with newest model
        formRef.current.resetForm({ values: scte });
        triggerEvent('preset-update');
      }
      setButtonState(undefined);
      return res;
    });
  };

  return (
    <Form
      initialValues={settings}
      defaultValidation={true}
      handleSubmit={handleSubmit}
      className="hai-mt-n8"
      restValidationProps={{
        enableReinitialize: true,
        validationSchema: scteValidationSchema(t),
        innerRef: formRef,
      }}
    >
      <FormContext.Consumer>
        {(formContext: FormikProps<ScteViewModel>) => {
          return (
            <>
              <DynamicContainer minColumns={2} maxColumns={2} className="mk-mt-panel-name-align">
                <FormTextfield name="name" mask title={t('general.name')} className="max-width" />
                {!sessionStore.isUser() && (
                  <div className="d-flex ml-auto hai-mt-panel-apply-sticky">
                    <Button className="hai-mr-6" onClick={onCancel}>
                      {t('general.cancel')}
                    </Button>
                    <Button
                      variant="primary"
                      onClick={() => validateFormContext(formContext, t)}
                      disabled={!formContext.dirty}
                      state={buttonState}
                    >
                      {t('general.apply')}
                    </Button>
                  </div>
                )}
              </DynamicContainer>
              <FormBuilder
                translationPrefixLabels="scte.settings"
                translationPrefixEnums="scte.enums"
                layout={FormLayout.THREE_COLUMNS_GRID}
                fields={[
                  {
                    type: FormFieldType.TITLE,
                    label: t('scte.settings.settings'),
                    className: 'hai-mb-0',
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'preEventReminders',
                    options: preEventReminderOptions,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'msgDeltaMs',
                    required: true,
                    hint: t('scte.settings.msgDeltaMsExplain'),
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'synchronizationType',
                    options: syncTypeOptions,
                  },
                  {
                    type: FormFieldType.INPUT_NUMBER,
                    name: 'leapSeconds',
                    hint: t('scte.settings.leapSecondsExplain'),
                  },
                  {
                    type: FormFieldType.DROPDOWN,
                    name: 'timeDescriptorPeriodSecs',
                    options: timeDescriptorPeriodOptions,
                  },
                ]}
              />
            </>
          );
        }}
      </FormContext.Consumer>
    </Form>
  );
};
